import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

import * as colors from 'styles/theme/themeCore';

export const themeConfig: ThemeConfig = {
    initialColorMode: 'dark',
    useSystemColorMode: false,
};

export const theme = extendTheme({
    config: themeConfig,
    colors: {
        cocoa: {
            0: colors.cocoas.cocoa0,
            100: colors.cocoas.cocoa100,
            200: colors.cocoas.cocoa200,
            300: colors.cocoas.cocoa300,
            400: colors.cocoas.cocoa400,
            500: colors.cocoas.cocoa500,
            600: colors.cocoas.cocoa600,
            700: colors.cocoas.cocoa700,
            800: colors.cocoas.cocoa800,
            900: colors.cocoas.cocoa900,
            950: colors.cocoas.cocoa950,
            1000: colors.cocoas.cocoa1000,
        },
        gold: {
            0: colors.golds.gold0,
            100: colors.golds.gold100,
            200: colors.golds.gold200,
            300: colors.golds.gold300,
            400: colors.golds.gold400,
            500: colors.golds.gold500,
            600: colors.golds.gold600,
        },
        green: {
            0: colors.greens.green0,
            100: colors.greens.green100,
            200: colors.greens.green200,
            300: colors.greens.green300,
            400: colors.greens.green400,
            500: colors.greens.green500,
            600: colors.greens.green600,
        },
        red: {
            0: colors.reds.red0,
            100: colors.reds.red100,
            200: colors.reds.red200,
            300: colors.reds.red300,
            400: colors.reds.red400,
            500: colors.reds.red500,
            600: colors.reds.red600,
        },
        purple: {
            0: colors.purples.purple0,
            100: colors.purples.purple100,
            200: colors.purples.purple200,
            300: colors.purples.purple300,
            400: colors.purples.purple400,
            500: colors.purples.purple500,
            600: colors.purples.purple600,
        },
        seafoam: {
            0: colors.seafoams.seafoam0,
            100: colors.seafoams.seafoam100,
            200: colors.seafoams.seafoam200,
            300: colors.seafoams.seafoam300,
            400: colors.seafoams.seafoam400,
            500: colors.seafoams.seafoam500,
            600: colors.seafoams.seafoam600,
        },
    },
    components: {
        Button: {
            baseStyle: {
                fontWeight: 'medium',
            },
        },
        Input: {
            defaultProps: {
                focusBorderColor: 'teal.400',
            },
        },
        Textarea: {
            defaultProps: {
                focusBorderColor: 'teal.400',
            },
        },
        Select: {
            defaultProps: {
                focusBorderColor: 'teal.400',
            },
        },
        NumberInput: {
            defaultProps: {
                focusBorderColor: 'teal.400',
            },
        },
        Drawer: {
            sizes: {
                huge: {
                    dialog: {
                        maxWidth: '90%',
                    },
                },
                xl: {
                    dialog: {
                        minWidth: '1000px',
                        maxWidth: '50%',
                    },
                },
            },
        },
        Modal: {
            sizes: {
                huge: {
                    dialog: {
                        maxWidth: '90%',
                    },
                },
            },
        },
    },
    styles: {
        global: {
            body: {
                fontFamily: 'halyard-text, sans-serif',
            },

            'div.chakra-popover__popper': {
                zIndex: 9999,
            },
            // SWITCH track color
            'span.chakra-switch__track[data-checked]': {
                background: 'teal.400',
            },

            // DATEPICKER
            '.react-date-picker': {
                width: '100%',

                '.react-date-picker__wrapper': {
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid',
                    paddingX: 2,
                    height: 8,
                    borderRadius: 'sm',
                    transitionProperty: 'common',
                    transitionDuration: 'normal',
                    borderColor: 'var(--chakra-colors-chakra-border-color);',

                    ':focus-within': {
                        borderColor: 'teal.300',
                        boxShadow: '0 0 0 1px var(--chakra-colors-teal-300)',
                    },

                    input: {
                        background: 'transparent',
                        outline: 'none',
                        fontSize: 'sm',
                    },
                    '.react-date-picker__inputGroup': {
                        marginRight: 1,
                    },
                    '.react-date-picker__inputGroup__leadingZero': {
                        fontSize: 'sm',
                        fontWeight: 'normal',
                    },

                    '.react-date-picker__inputGroup__divider': {
                        paddingX: 1,
                    },

                    '.react-date-picker__button': {
                        display: 'flex',

                        svg: {
                            stroke: 'white',
                            boxSize: '12px',
                        },
                    },
                    '.react-date-picker__clear-button': {
                        marginLeft: 'auto',
                        marginRight: 2,
                    },
                },
                '.react-date-picker__calendar': {
                    zIndex: 100,
                    width: '100%',
                    minWidth: 'fit-content',
                },
                '.react-date-picker__calendar--closed': {
                    display: 'none',
                },
                '.react-calendar': {
                    background: 'gray.600',
                    borderRadius: 'md',
                    padding: 2,
                    marginTop: 1,
                    boxShadow: 'dark-lg',
                    width: '350px',

                    '.react-calendar__navigation': {
                        display: 'flex',
                        marginBottom: 2,

                        '.react-calendar__navigation__arrow': {
                            height: 8,
                            width: 8,
                            borderRadius: 'md',
                            transitionProperty: 'common',
                            transitionDuration: 'normal',

                            ':hover': {
                                background: 'gray.700',
                            },
                        },
                    },

                    '.react-calendar__viewContainer': {
                        fontSize: 'sm',
                    },

                    '.react-calendar__tile': {
                        paddingY: 1,
                    },

                    '.react-calendar__month-view': {
                        '.react-calendar__month-view__weekdays__weekday': {
                            textAlign: 'center',
                        },

                        '.react-calendar__month-view__days__day': {
                            fontWeight: 'semibold',
                            border: '1px solid transparent',
                            borderRadius: 'md',
                            transitionProperty: 'common',
                            transitionDuration: 'faster',

                            ':hover': {
                                borderColor: 'var(--chakra-colors-chakra-border-color);',
                            },
                        },
                        '.react-calendar__month-view__days__day--weekend': {
                            color: 'red.200',
                            fontWeight: 'light',
                        },
                        '.react-calendar__month-view__days__day--neighboringMonth': {
                            color: 'whiteAlpha.500',
                            fontWeight: 'light',
                        },
                        '.react-calendar__tile--active': {
                            background: 'teal.300',
                            color: 'gray.800',
                            fontWeight: 'semibold',
                        },
                    },
                },
            },
        },
    },
});
